import ADDRESS_FORMATS from './addressFormats.json';

// Country-aware Address layouts (#157). Every Address has the same parts —
// two street lines, District, locality, region, Postcode — and its Country
// decides which show, what each is called, which are required and their
// order. Layouts come from Google's address data, bundled in
// addressFormats.json (rebuilt by scripts/build-address-formats.js, never
// edited by hand). Everything here only warns: a flagged Address still
// saves.
//
// Part keys are the Commercial Entity Address column names; Company Info
// maps its own address_* columns onto them (COMPANY_INFO_FIELDS below).

// Google's format letters → Optim's parts.
const PART_FOR_LETTER = { D: 'district', C: 'suburb', S: 'state', Z: 'postcode' };
const LETTER_FOR_PART = { district: 'D', suburb: 'C', state: 'S', postcode: 'Z' };

const ZIP_LABELS = { postal: 'Postcode', zip: 'ZIP Code', eircode: 'Eircode', pin: 'PIN Code' };
const STATE_LABELS = {
  area: 'Area', county: 'County', department: 'Department', district: 'District', do_si: 'Do/Si',
  emirate: 'Emirate', island: 'Island', oblast: 'Oblast', parish: 'Parish', prefecture: 'Prefecture',
  province: 'Province', state: 'State',
};
const LOCALITY_LABELS = { city: 'City', district: 'District', post_town: 'Post Town', suburb: 'Suburb' };
const SUBLOCALITY_LABELS = {
  suburb: 'Suburb', district: 'District', neighborhood: 'Neighbourhood', village_township: 'Village/Township',
  townland: 'Townland',
};

// A country without a layout of its own: every part shows, none is
// required and nothing is checked, so its parts are accepted as typed.
const GENERAL_LAYOUT = {
  order: ['suburb', 'state', 'postcode'],
  labels: { suburb: 'City', state: 'State/Province', postcode: 'Postcode' },
  required: [],
};

const STREET_PARTS = [
  { key: 'line1', label: 'Line 1' },
  { key: 'line2', label: 'Line 2' },
];

function layoutFor(country) {
  const data = ADDRESS_FORMATS[country];
  if (!data) return null;
  const order = [];
  for (const m of data.fmt.matchAll(/%([A-Z])/g)) {
    const part = PART_FOR_LETTER[m[1]];
    if (part && !order.includes(part)) order.push(part);
  }
  return {
    data,
    order,
    labels: {
      district: SUBLOCALITY_LABELS[data.sublocality_name_type || 'suburb'] || 'District',
      suburb: LOCALITY_LABELS[data.locality_name_type || 'city'] || 'City',
      state: STATE_LABELS[data.state_name_type || 'province'] || 'Province',
      postcode: ZIP_LABELS[data.zip_name_type || 'postal'] || 'Postcode',
    },
    required: [...data.require].map((l) => PART_FOR_LETTER[l]).filter(Boolean),
  };
}

// The parts an Address in `country` shows, in order: street lines first,
// then the country's own. Each part: { key, label, required, options? },
// where options is the country's fixed State/Province list.
export function addressParts(country) {
  const layout = layoutFor(country);
  const { order, labels, required } = layout || GENERAL_LAYOUT;
  const streetRequired = !!layout && layout.data.require.includes('A');
  return [
    ...STREET_PARTS.map((p, i) => ({ ...p, required: i === 0 && streetRequired })),
    ...order.map((key) => ({
      key,
      label: labels[key],
      required: required.includes(key),
      ...(key === 'state' && layout?.data.regions ? { options: layout.data.regions } : {}),
    })),
  ];
}

export function partLabel(country, key) {
  return addressParts(country).find((p) => p.key === key)?.label;
}

// The value on the country's list matching `value` (by stored value or by
// name, ignoring case), or undefined.
export function findRegion(country, value) {
  const regions = ADDRESS_FORMATS[country]?.regions;
  if (!regions || !value) return undefined;
  const v = String(value).trim().toLowerCase();
  return regions.find((r) => r.value.toLowerCase() === v || r.label.toLowerCase() === v);
}

const blank = (v) => !String(v || '').trim();

// A message when the Postcode doesn't fit its country's pattern, else
// undefined. Never for Australia, whose Suburb and Postcode Australia Post
// checks instead (#158, postcodeCheck.js).
export function postcodeWarning(address) {
  const country = address.country;
  const layout = layoutFor(country);
  const value = String(address.postcode || '').trim();
  if (!layout?.data.zip || !value || country === 'Australia') return undefined;
  if (new RegExp(`^(?:${layout.data.zip})$`, 'i').test(value)) return undefined;
  return `Doesn't look like a ${layout.labels.postcode} for ${country}.`;
}

// A street line or Suburb (or City) with the first letter of each word
// capitalised and the rest as typed (#158): "kingswood" reads Kingswood,
// "1 john st" 1 John St, McMahons Point stays as it is. Applied as the field is left; the server does the same
// on save (backend/services/addressFormat.js).
export function capitaliseWords(value) {
  if (value == null) return value;
  return String(value).replace(/(^|[\s-])(\p{Ll})/gu, (m, sep, ch) => sep + ch.toUpperCase());
}

// The first required part left blank, as its label, or undefined.
export function missingPart(address) {
  return addressParts(address.country).find((p) => p.required && blank(address[p.key]))?.label;
}

// { partKey: message } for each part that needs a second look: a required
// part left blank, a Postcode outside its country's pattern (outside
// Australia), or a State/Province that isn't on the country's list.
export function addressWarnings(address) {
  const country = address.country;
  const layout = layoutFor(country);
  if (!layout) return {};
  const warnings = {};
  for (const part of addressParts(country)) {
    const value = address[part.key];
    if (part.required && blank(value)) {
      warnings[part.key] = `${part.label} is missing.`;
    } else if (part.key === 'postcode' && postcodeWarning(address)) {
      warnings[part.key] = postcodeWarning(address);
    } else if (part.options && !blank(value) && !findRegion(country, value)) {
      warnings[part.key] = `Not on the ${part.label} list. Pick one.`;
    }
  }
  return warnings;
}

// Company Info's own column for each part. Its Street is the first street
// line and Unit/Suite the second.
export const COMPANY_INFO_FIELDS = {
  line1: 'address_street', line2: 'address_unit', district: 'address_district', suburb: 'address_city',
  state: 'address_state', postcode: 'address_postcode',
};

export function companyInfoAddress(data) {
  const address = { country: data.country };
  for (const [key, field] of Object.entries(COMPANY_INFO_FIELDS)) address[key] = data[field];
  return address;
}

// What changing an Address from its current Country to `newCountry` does
// to its parts: the ones both share are kept, the ones `newCountry` doesn't
// show are blanked. Returns only the parts that change.
export function partsDroppedFor(newCountry) {
  const shown = new Set(addressParts(newCountry).map((p) => p.key));
  const dropped = {};
  for (const key of Object.keys(LETTER_FOR_PART)) if (!shown.has(key)) dropped[key] = '';
  return dropped;
}

// The Address as lines in its country's order, street first, Country left
// off (#395): a Chinese factory reads "No. 5 Xing'an Road", "Sihui City",
// "Zhaoqing", "Guangdong, 526241". backend/services/addressFormat.js
// mirrors this.
const GENERAL_FMT = '%A%n%C%n%S %Z';
export function addressLines(address) {
  if (!address) return [];
  const fmt = layoutFor(address.country)?.data.fmt || GENERAL_FMT;
  const value = (key) => String(address[key] || '').trim();
  const street = [value('line1'), value('line2')].filter(Boolean).join('\n');
  const filled = fmt
    .replace(/%A/g, street)
    .replace(/%([A-Z])/g, (m, letter) => (PART_FOR_LETTER[letter] ? value(PART_FOR_LETTER[letter]) : ''))
    .replace(/%n/g, '\n');
  return filled.split('\n')
    .map((line) => line.replace(/\s+/g, ' ').replace(/^[\s,\-–]+|[\s,\-–]+$/g, '').trim())
    .filter(Boolean);
}
